/* import __COLOCATED_TEMPLATE__ from './outbound-campaign-edit-panel.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type { ParseResult } from 'papaparse';
import csvParser from 'papaparse';
import { Promise as EmberPromise } from 'rsvp';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import ajax, { get } from 'embercom/lib/ajax';
import type PhoneNumber from 'embercom/models/calling-phone-number';
import type { FinVoiceProcedure } from 'embercom/objects/ai-agent/fin-voice/fin-voice-runbook-options';
import {
  FIN_VOICE_11LABS_VOICES,
  getVoicesForLanguage,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import type { Campaign } from './outbound-campaign-list';

interface OutboundCampaignEditPanelSignature {
  Args: {
    campaign: Campaign | null;
    phoneNumbers: Record<string, PhoneNumber>;
    onSave: () => void;
    onClose: () => void;
  };
}

const MAX_CSV_ROWS = 100;

export default class OutboundCampaignEditPanel extends Component<OutboundCampaignEditPanelSignature> {
  @service declare intl: IntlService;
  @service declare appService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @tracked campaignName = this.args.campaign?.name || '';
  @tracked selectedPhoneNumberId: string | undefined = undefined;
  @tracked file: File | null = null;
  @tracked fileIsValid = false;
  @tracked fileFailedToUpload = false;
  @tracked parsingErrorMessage = '';
  @tracked parsedRows: { phone_number: string }[] = [];
  @tracked validationErrors: string[] = [];
  @tracked isParsed = false;

  @tracked isSaving = false;
  @tracked isSending = false;
  @tracked showSendConfirmModal = false;

  @tracked procedures: FinVoiceProcedure[] = [];
  @tracked isLoadingProcedures = false;
  @tracked selectedProcedureId: string | undefined = undefined;

  @tracked selectedVoice: string | undefined = undefined;
  @tracked voicemailEnabled = false;
  @tracked voicemailMessage = '';

  private readonly E164_REGEX = /^\+[1-9]\d{1,14}$/;

  constructor(owner: unknown, args: OutboundCampaignEditPanelSignature['Args']) {
    super(owner, args);
    this.initPhoneNumber();
    this.initProcedure();
    this.initVoiceAndVoicemail();
    this.loadProcedures();
  }

  get isEditing() {
    return !!this.args.campaign;
  }

  get isDraft() {
    return !this.args.campaign || this.args.campaign.status === 'draft';
  }

  get panelTitle() {
    return this.isEditing
      ? this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.edit-title')
      : this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.new-title');
  }

  get phoneNumberOptions(): { value: string; text: string }[] {
    let phoneNumbers = this.args.phoneNumbers;
    if (!phoneNumbers) {
      return [];
    }

    return Object.entries(phoneNumbers).map(([id, phoneNumber]) => {
      let number = phoneNumber.formattedPhoneNumber || phoneNumber.phoneNumber;
      return {
        value: id,
        text: phoneNumber.name ? `${phoneNumber.name} (${number})` : number,
      };
    });
  }

  get voiceOptions(): { value: string; text: string; description: string }[] {
    return getVoicesForLanguage('en-US').map((voice) => ({
      value: voice.value,
      text: voice.text,
      description: voice.description.map((key) => this.intl.t(key)).join(' '),
    }));
  }

  get selectedVoiceDescription(): string {
    if (!this.selectedVoice) {
      return '';
    }
    let voice = FIN_VOICE_11LABS_VOICES.find((v) => v.value === this.selectedVoice);
    if (!voice) {
      return '';
    }
    return voice.description.map((key) => this.intl.t(key)).join(' ');
  }

  get hasVoicemailValidationError(): boolean {
    return this.voicemailEnabled && !this.voicemailMessage.trim();
  }

  get campaignPayload(): Record<string, string | boolean | undefined> {
    return {
      app_id: this.appService.app.id,
      name: this.campaignName.trim(),
      workspace_phone_number_id: this.validSelectedPhoneNumberId,
      procedure_id: this.validSelectedProcedureId,
      voice: this.selectedVoice,
      voicemail_enabled: this.voicemailEnabled,
      voicemail_message: this.voicemailMessage.trim() || undefined,
    };
  }

  get canSaveDraft(): boolean {
    return !!this.campaignName.trim();
  }

  get canSave(): boolean {
    return (
      this.canSaveDraft && !!this.validSelectedPhoneNumberId && !!this.validSelectedProcedureId
    );
  }

  get canSend(): boolean {
    return (
      this.isDraft &&
      this.canSave &&
      this.isParsed &&
      this.parsedRows.length > 0 &&
      this.validationErrors.length === 0 &&
      !this.hasVoicemailValidationError
    );
  }

  get rowCount() {
    return this.parsedRows.length;
  }

  get hasValidationErrors() {
    return this.validationErrors.length > 0;
  }

  get hasExistingCsvData(): boolean {
    return !this.isDraft && !!this.args.campaign && this.args.campaign.total_rows > 0;
  }

  get sendConfirmBody() {
    return this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.send-confirm', {
      count: this.parsedRows.length,
    });
  }

  get procedureOptions(): { value: string; text: string }[] {
    return this.procedures.map((proc) => ({
      value: proc.id,
      text: proc.name,
    }));
  }

  get validSelectedPhoneNumberId(): string | undefined {
    if (!this.selectedPhoneNumberId) {
      return undefined;
    }
    return this.phoneNumberOptions.some((o) => o.value === this.selectedPhoneNumberId)
      ? this.selectedPhoneNumberId
      : undefined;
  }

  get selectedPhoneNumberText(): string {
    if (!this.validSelectedPhoneNumberId) {
      return this.intl.t(
        'ai-agent.deploy.phone-overview.outbound-campaigns.assigned-to-placeholder',
      );
    }
    let option = this.phoneNumberOptions.find((o) => o.value === this.validSelectedPhoneNumberId);
    return option?.text || '';
  }

  get isPhoneNumberPlaceholder(): boolean {
    return !this.validSelectedPhoneNumberId;
  }

  get selectedProcedureText(): string {
    if (!this.validSelectedProcedureId) {
      return this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.procedure-placeholder');
    }
    let option = this.procedureOptions.find((o) => o.value === this.validSelectedProcedureId);
    return option?.text || '';
  }

  get isProcedurePlaceholder(): boolean {
    return !this.validSelectedProcedureId;
  }

  get validSelectedProcedureId(): string | undefined {
    if (!this.selectedProcedureId) {
      return undefined;
    }
    return this.procedures.some((p) => p.id === this.selectedProcedureId)
      ? this.selectedProcedureId
      : undefined;
  }

  private initPhoneNumber() {
    if (!this.args.campaign?.workspace_phone_number_id) {
      return;
    }
    this.selectedPhoneNumberId = String(this.args.campaign.workspace_phone_number_id);
  }

  private initProcedure() {
    if (this.args.campaign?.procedure_id) {
      this.selectedProcedureId = this.args.campaign.procedure_id;
    }
  }

  private initVoiceAndVoicemail() {
    if (this.args.campaign?.voice) {
      this.selectedVoice = this.args.campaign.voice;
    }
    if (this.args.campaign) {
      this.voicemailEnabled = this.args.campaign.voicemail_enabled || false;
      this.voicemailMessage = this.args.campaign.voicemail_message || '';
    }
  }

  private async loadProcedures() {
    this.isLoadingProcedures = true;
    try {
      let response = await get('/ember/fin_voice/procedures', {
        app_id: this.appService.app.id,
        state: 'live',
      });
      this.procedures = response as FinVoiceProcedure[];
    } catch {
      this.procedures = [];
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.procedure-load-error'),
      );
    } finally {
      this.isLoadingProcedures = false;
    }
  }

  @action
  onNameChange(event: Event) {
    this.campaignName = (event.target as HTMLInputElement).value;
  }

  @action
  onPhoneNumberSelect(value: string) {
    this.selectedPhoneNumberId = value || undefined;
  }

  @action
  onProcedureSelect(value: string) {
    this.selectedProcedureId = value || undefined;
  }

  @action
  onVoiceSelect(value: string) {
    this.selectedVoice = value || undefined;
  }

  @action
  onVoicemailToggle() {
    this.voicemailEnabled = !this.voicemailEnabled;
  }

  @action
  onVoicemailMessageChange(event: Event) {
    this.voicemailMessage = (event.target as HTMLTextAreaElement).value;
  }

  @action
  onBlanketClick(event: MouseEvent) {
    if ((event.target as HTMLElement).hasAttribute('data-side-drawer-blanket')) {
      this.args.onClose();
    }
  }

  @action
  onCsvFileSet(file: File | null) {
    this.file = file;
    this.fileIsValid = file !== null;
    this.isParsed = false;
    this.parsedRows = [];
    this.validationErrors = [];
    this.parsingErrorMessage = '';
    this.fileFailedToUpload = false;

    if (file) {
      this.parseCsvFile();
    }
  }

  @action
  async parseCsvFile() {
    if (!this.file) {
      return;
    }

    try {
      this.parsingErrorMessage = '';
      this.validationErrors = [];
      let results = await this.parseRawCsvFile(this.file);

      if (results.data.length === 0) {
        this.parsingErrorMessage = this.intl.t(
          'ai-agent.deploy.phone-overview.outbound-csv-upload.validation-error-missing-phone',
        );
        this.fileFailedToUpload = true;
        return;
      }

      let headers = results.meta.fields || [];

      if (!headers.includes('phone_number')) {
        this.parsingErrorMessage = this.intl.t(
          'ai-agent.deploy.phone-overview.outbound-csv-upload.validation-error-missing-phone',
        );
        this.fileFailedToUpload = true;
        return;
      }

      if (results.data.length > MAX_CSV_ROWS) {
        this.parsingErrorMessage = this.intl.t(
          'ai-agent.deploy.phone-overview.outbound-csv-upload.validation-error-row-limit',
          { limit: MAX_CSV_ROWS },
        );
        this.fileFailedToUpload = true;
        return;
      }

      let rows: { phone_number: string }[] = [];
      let seenPhoneNumbers = new Set<string>();
      let errors: string[] = [];

      for (let i = 0; i < results.data.length; i++) {
        let row = results.data[i];
        let phoneNumber = row['phone_number']?.trim() ?? '';

        if (!this.E164_REGEX.test(phoneNumber)) {
          errors.push(
            this.intl.t(
              'ai-agent.deploy.phone-overview.outbound-csv-upload.validation-error-invalid-phone',
              { row: i + 1 },
            ),
          );
        } else if (seenPhoneNumbers.has(phoneNumber)) {
          errors.push(
            this.intl.t(
              'ai-agent.deploy.phone-overview.outbound-csv-upload.validation-error-duplicate-phone',
              { row: i + 1, phone: phoneNumber },
            ),
          );
        } else {
          seenPhoneNumbers.add(phoneNumber);
          rows.push({ phone_number: phoneNumber });
        }
      }

      this.validationErrors = errors;
      this.parsedRows = rows;
      this.isParsed = true;
    } catch (e: unknown) {
      this.fileFailedToUpload = true;
      this.parsingErrorMessage = this.intl.t(
        'ai-agent.deploy.phone-overview.outbound-csv-upload.validation-error-missing-phone',
      );
    }
  }

  @action
  async saveDraft() {
    if (!this.canSaveDraft) {
      return;
    }

    this.isSaving = true;
    try {
      await this.saveOrCreateCampaign();

      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.save-success'),
      );
      this.args.onSave();
    } catch (e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.save-error'),
      );
    } finally {
      this.isSaving = false;
    }
  }

  @action
  openSendConfirmModal() {
    if (!this.canSend) {
      return;
    }
    this.showSendConfirmModal = true;
  }

  @action
  closeSendConfirmModal() {
    this.showSendConfirmModal = false;
  }

  @action
  async confirmSendCampaign() {
    this.showSendConfirmModal = false;

    if (!this.canSend || !this.file) {
      return;
    }

    this.isSending = true;
    try {
      let campaignId = await this.saveOrCreateCampaign();

      // Send the campaign with the CSV
      let formData = new FormData();
      formData.append('app_id', this.appService.app.id);
      formData.append('csv_file', this.file);

      await ajax({
        type: 'POST',
        url: `/ember/fin_voice/outbound_campaigns/${campaignId}/send_campaign`,
        data: formData,
        processData: false,
        contentType: false,
      });

      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.send-success'),
      );
      this.args.onSave();
    } catch (e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.send-error'),
      );
    } finally {
      this.isSending = false;
    }
  }

  private async saveOrCreateCampaign(): Promise<number> {
    let campaignId: number;

    if (this.isEditing) {
      campaignId = this.args.campaign!.id;
      await ajax({
        type: 'PUT',
        url: `/ember/fin_voice/outbound_campaigns/${campaignId}`,
        data: JSON.stringify(this.campaignPayload),
      });
    } else {
      let response: { campaign: { id: number } } = await ajax({
        type: 'POST',
        url: '/ember/fin_voice/outbound_campaigns',
        data: JSON.stringify(this.campaignPayload),
      });
      campaignId = response.campaign.id;
    }

    return campaignId;
  }

  private async parseRawCsvFile(file: File): Promise<ParseResult<Record<string, string>>> {
    return new EmberPromise((resolve, reject) => {
      let complete = (results: ParseResult<Record<string, string>>) => {
        if (results.errors.length) {
          if (
            results.errors.some((error: { code: string }) => error.code !== 'UndetectableDelimiter')
          ) {
            reject(results);
            return;
          }
        }
        resolve(results);
      };
      csvParser.parse(file, { header: true, skipEmptyLines: true, complete, error: reject });
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Deploy::Voice::OutboundCampaignEditPanel': typeof OutboundCampaignEditPanel;
    'ai-agent/deploy/voice/outbound-campaign-edit-panel': typeof OutboundCampaignEditPanel;
  }
}
